import {
  labelTemplates,
  qualifyingStatuses,
  issueDetectors,
} from "../../constants";

import { matchKeywords } from "../../queryHelpers";

/**
 * Gets a basic category label based on category type and status
 * Use this for categories with simple static labels
 */
export function getBaseCategoryLabel(
  categoryType: string,
  status: string
): string {
  const baseLabels = labelTemplates[categoryType] || labelTemplates.rental;
  return baseLabels[status] || "Status Unknown";
}

/**
 * Generate a detailed label based on outcome details
 * Used for categories where we want to analyze specific issues from Lumen
 */
export function getOutcomeBasedLabel(
  categoryType: string,
  status: string,
  outcomes: Array<{ name: string }>
): string {
  // Skip if status doesn't qualify or no outcomes available
  const categoryQualifiers = qualifyingStatuses[categoryType];
  if (
    !categoryQualifiers ||
    !categoryQualifiers.includes(status) ||
    !outcomes ||
    !Array.isArray(outcomes) ||
    outcomes.length === 0
  ) {
    return null;
  }

  // Get the appropriate detectors for this category
  const detectors = issueDetectors[categoryType];
  if (!detectors || detectors.length === 0) {
    return null;
  }

  // Detect issues for this category using our generalized function
  const issues = detectors
    .filter((detector) =>
      matchKeywords(outcomes, detector.keywords, {
        property: "name",
        matchAll: true,
        matchWholeWords: false,
        caseSensitive: false,
        checkProperties: [],
      })
    )
    .map((detector) => detector.label);

  // Return comma-separated issues if any were found
  if (issues.length > 0) {
    return issues.join(", ");
  }

  return null;
}

/**
 * Determines whether to use "Total" or "Avg" prefix based on formula name
 * @param formulaName The formula name to check
 * @returns The appropriate label prefix
 */
export function getLabelPrefix(formulaName: string | null | undefined): string {
  if (!formulaName || typeof formulaName !== "string") {
    return "Avg"; // Default to "Avg" if no formula name available
  }

  const formulaLower = formulaName.toLowerCase();
  if (formulaLower.includes("total")) {
    return "Total";
  }

  if (formulaLower.includes("average") || formulaLower.includes("avg")) {
    return "Avg";
  }

  return "Avg"; // Default to "Avg" for any other cases
}

/**
 * Creates a numeric label with appropriate prefix based on formula name
 * @param type The category type (income, credit)
 * @param value The numeric value to display
 * @param formulaName The formula name to determine prefix from
 * @returns A formatted label with the appropriate prefix
 */
export function getNumericLabel(
  type: string,
  value: number,
  formulaName: string | null | undefined
): string {
  const prefix = getLabelPrefix(formulaName);

  if (type === "income-to-rent") {
    return `${prefix} Income to Rent Ratio: ${value}`;
  }

  if (type === "cash-runway") {
    return `Cash Runway Ratio: ${value}`;
  }

  if (type === "credit") {
    return `${prefix} Credit Score: ${value}`;
  }

  return `${prefix} Value: ${value}`;
}
